import * as React from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Typography,
} from "@mui/material";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import FaceIcon from "@mui/icons-material/Face";
import { Talent } from "../../types/users";
import { useTitle } from "../../hooks/useTitle";
import TalentDrawer from "./TalentDrawer";
import {
  GoogleMap,
  useLoadScript,
  OverlayView,
} from "@react-google-maps/api";
import { GMAP_API_KEY } from "../../configs/api";
import { useGetTalentsQuery } from "../../services/talent.service";
import { getDistanceBetweenPoints } from "../../utils/mapHelper";
import { CardBase } from "../../components/global";

const mapContainerStyle: React.CSSProperties = {
  width: "100%",
  height: "85vh",
  borderRadius: "8px",
};

const options = {
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
};

const CustomMarker = (props: any) => {
  const { name, photo, onClick } = props;

  return (
    <OverlayView {...props}>
      <Box sx={{ marginTop: -2.5, marginLeft: -2.5 }}>
        <Avatar
          alt={name}
          src={photo?.[0]?.original_url}
          onClick={onClick}
          sx={{
            backgroundColor: "#F4A259",
            cursor: "pointer",
            border: "2px solid white",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            "&:hover": {
              transform: "scale(1.1)",
              transition: "transform 0.2s",
            },
          }}
        >
          {name?.charAt(0)?.toUpperCase()}
        </Avatar>
      </Box>
    </OverlayView>
  );
};

//Center coordinates for US
const DEFAULT_LAT = 37.0902;
const DEFAULT_LNG = -95.7129;

//see all US
const DEFAULT_ZOOM = 5;
const DEFAULT_RADIUS = 11000; //m --> changed according to DEFAULT_ZOOM
const mapLibraries = ["geometry"];

const TalentsMapPage = () => {
  useTitle("Talents Map");
  const [center, setCenter] = React.useState({
    lat: DEFAULT_LAT,
    lng: DEFAULT_LNG,
  });
  const [searchParam, setSearchParam] = React.useState({
    lat: DEFAULT_LAT,
    lng: DEFAULT_LNG,
    radius: DEFAULT_RADIUS,
  });

  const { data, isFetching, isLoading } = useGetTalentsQuery({
    page: 0,
    pageSize: 100,
    distance: `${searchParam.lat}|${searchParam.lng}|${searchParam.radius}`,
  });

  const [open, setOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState<Talent | null>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GMAP_API_KEY,
    //@ts-ignore
    libraries: mapLibraries,
  });

  const mapRef = React.useRef<google.maps.Map>();

  const onMapLoad = React.useCallback(
    (map: google.maps.Map) => {
      map.setCenter(center);
      mapRef.current = map;
      // Trigger initial search after map is loaded
      setTimeout(() => {
        if (mapRef.current) {
          const bounds = mapRef.current.getBounds();
          if (bounds) {
            const lat = mapRef.current.getCenter()?.lat() || DEFAULT_LAT;
            const lng = mapRef.current.getCenter()?.lng() || DEFAULT_LNG;
            const nsRadius = google.maps.geometry.spherical.computeDistanceBetween(
              bounds.getCenter().toJSON(),
              {
                lat: bounds.getNorthEast().toJSON().lat,
                lng: bounds.getCenter().toJSON().lng,
              }
            );
            const ewRadius = google.maps.geometry.spherical.computeDistanceBetween(
              bounds.getCenter().toJSON(),
              {
                lat: bounds.getCenter().toJSON().lat,
                lng: bounds.getNorthEast().toJSON().lng,
              }
            );
            const radius = nsRadius >= ewRadius ? nsRadius : ewRadius;
            setSearchParam({ lat, lng, radius: Number(radius.toFixed(0)) || DEFAULT_RADIUS });
          }
        }
      }, 100);
    },
    [center]
  );

  const getRadius = React.useCallback(() => {
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds();
      if (!bounds) return null;

      // computeDistanceBetween returns meters
      const nsRadius = google.maps.geometry.spherical.computeDistanceBetween(
        bounds.getCenter().toJSON(),
        {
          lat: bounds.getNorthEast().toJSON().lat,
          lng: bounds.getCenter().toJSON().lng,
        }
      );
      // console.log(`nsRadius: ${nsRadius.toFixed(2)}`);
      const ewRadius = google.maps.geometry.spherical.computeDistanceBetween(
        bounds.getCenter().toJSON(),
        {
          lat: bounds.getCenter().toJSON().lat,
          lng: bounds.getNorthEast().toJSON().lng,
        }
      );
      const radius = nsRadius >= ewRadius ? nsRadius : ewRadius;
      return Number(radius.toFixed(0));
    }
  }, [mapRef.current]);

  const onDragEnd = React.useCallback(() => {
    if (!mapRef.current) return;
    const lat = mapRef.current?.getCenter()?.lat() || 0;
    const lng = mapRef.current?.getCenter()?.lng() || 0;
    const dist = getDistanceBetweenPoints(
      { lat, lng },
      { lat: center.lat, lng: center.lng }
    );
    if (dist >= 1) {
      // if center changed more than 1 mile
      setCenter({ lat, lng });
      setSearchParam({ lat, lng, radius: getRadius() || DEFAULT_RADIUS });
    }
  }, [getRadius]);

  const onZoomChanged = React.useCallback(() => {
    if (!mapRef.current) return;
    const lat = mapRef.current?.getCenter()?.lat() || 0;
    const lng = mapRef.current?.getCenter()?.lng() || 0;
    setCenter({ lat, lng });
    setSearchParam({ lat, lng, radius: getRadius() || DEFAULT_RADIUS });
  }, [getRadius]);

  const handleMarkerClick = (item: Talent) => {
    setCurrentItem(item);
    setOpen(true);
  };

  if (loadError) {
    return (
      <CardBase useGlassCard={false}>
        <Alert severity="error">
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            ERROR
          </Typography>
          Error loading maps. Please try again later.
        </Alert>
      </CardBase>
    );
  }

  if (!isLoaded) {
    return (
      <CardBase useGlassCard={false} sx={{ height: "85vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading Maps...
          </Typography>
        </Box>
      </CardBase>
    );
  }

  return (
    <CardBase
      useGlassCard={false}
      sx={{
        width: "100%",
        height: "85vh",
        p: 0,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "85vh",
          borderRadius: 1,
          overflow: "hidden",
          "& .gm-style": {
            borderRadius: 1,
          },
        }}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          // center={center}
          zoom={DEFAULT_ZOOM}
          options={options}
          onDragEnd={onDragEnd}
          onZoomChanged={onZoomChanged}
          onLoad={onMapLoad}
        >
          <React.Fragment>
            <Box
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                zIndex: 1000,
                pointerEvents: "none",
              }}
            >
              {(isLoading || isFetching) ? (
                <Chip
                  icon={<CircularProgress size={18} color="inherit" />}
                  label="Searching talents..."
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    fontWeight: 500,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    pointerEvents: "auto",
                  }}
                />
              ) : (
                <>
                  {!data?.meta?.total ? (
                    <Chip
                      label="Talents not found, try moving to another location or change zoom level"
                      color="warning"
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        backdropFilter: "blur(10px)",
                        fontWeight: 500,
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        pointerEvents: "auto",
                      }}
                    />
                  ) : (
                    <Chip
                      icon={<FaceIcon sx={{ color: "#F4A259" }} />}
                      label={`${data?.meta?.total || 0} talents found (max: 100 per map)`}
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        backdropFilter: "blur(10px)",
                        fontWeight: 600,
                        color: "#F4A259",
                        border: "1px solid rgba(244, 162, 89, 0.2)",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        pointerEvents: "auto",
                      }}
                    />
                  )}
                </>
              )}
            </Box>
            <OverlayView
              key={"#company-marker"}
              position={{
                lat: DEFAULT_LAT,
                lng: DEFAULT_LNG,
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <Box sx={{ marginTop: -2.5, marginLeft: -2.5 }}>
                <Chip
                  icon={<HomeRepairServiceIcon sx={{ color: "#ffffff" }} />}
                  label="Workz360"
                  sx={{
                    backgroundColor: "#F4A259",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                    border: "2px solid white",
                  }}
                />
              </Box>
            </OverlayView>

            {data?.data.map((talent) => (
              <CustomMarker
                id={talent.id}
                key={`#talent-marker-${talent.id}`}
                name={talent.full_name}
                photo={talent.photo}
                position={{
                  lat: talent.coordinate?.coordinates[1] || 0,
                  lng: talent.coordinate?.coordinates[0] || 0,
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                onClick={() => handleMarkerClick(talent)}
              />
            ))}
          </React.Fragment>
        </GoogleMap>
      </Box>
      <TalentDrawer data={currentItem!} open={open} setOpen={setOpen} />
    </CardBase>
  );
};
export default TalentsMapPage;
